/**
 * MultiCartProvider
 *
 * Context provider for multi-cart state management.
 * Provides cart data, drawer state, and real-time sync.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { MerchantCartSummary } from '@teletienda/shared-ui-lib';
import { useMultiCart } from './useMultiCart';
import { useMultiCartSync } from './useMultiCartSync';

/**
 * Context value for multi-cart functionality
 */
export interface MultiCartContextValue {
  /** Whether the drawer is open */
  isDrawerOpen: boolean;
  /** Open the cart drawer */
  openDrawer: () => void;
  /** Close the cart drawer */
  closeDrawer: () => void;
  /** Toggle the cart drawer */
  toggleDrawer: () => void;
  /** Cart summaries from all merchants */
  carts: MerchantCartSummary[];
  /** Total items across all carts */
  totalItems: number;
  /** Total amount in user's currency */
  totalAmount: number;
  /** User's preferred currency */
  currency: string;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch cart data */
  refetch: () => void;
}

const MultiCartContext = createContext<MultiCartContextValue | null>(null);

/**
 * Props for MultiCartProvider
 */
export interface MultiCartProviderProps {
  children: ReactNode;
}

/**
 * Provider component for multi-cart state
 *
 * Wraps the app to provide cart data and drawer state.
 * Automatically subscribes to real-time cart updates.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <MultiCartProvider>
 *       <Header />
 *       <MainContent />
 *     </MultiCartProvider>
 *   );
 * }
 * ```
 */
export function MultiCartProvider({ children }: MultiCartProviderProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { data, isLoading, error, refetch } = useMultiCart();

  // Subscribe to real-time updates
  useMultiCartSync();

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setIsDrawerOpen((prev) => !prev), []);

  const value: MultiCartContextValue = {
    isDrawerOpen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    carts: data?.carts ?? [],
    totalItems: data?.totalItems ?? 0,
    totalAmount: data?.totalAmount ?? 0,
    currency: data?.currency ?? 'ARS',
    isLoading,
    error: error as Error | null,
    refetch: () => void refetch(),
  };

  return (
    <MultiCartContext.Provider value={value}>
      {children}
    </MultiCartContext.Provider>
  );
}

/**
 * Hook to access multi-cart context
 *
 * Must be used within MultiCartProvider.
 *
 * @returns MultiCartContextValue
 * @throws Error if used outside MultiCartProvider
 *
 * @example
 * ```tsx
 * function CartIcon() {
 *   const { totalItems, openDrawer } = useMultiCartContext();
 *
 *   return (
 *     <ActionIcon onClick={openDrawer}>
 *       <IconShoppingCart />
 *       {totalItems > 0 && <Badge>{totalItems}</Badge>}
 *     </ActionIcon>
 *   );
 * }
 * ```
 */
export function useMultiCartContext(): MultiCartContextValue {
  const context = useContext(MultiCartContext);
  if (!context) {
    throw new Error('useMultiCartContext must be used within MultiCartProvider');
  }
  return context;
}
